import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../config/api';
import Header from './Header';
import Footer from './Footer';
import './PrivacyPolicy.css';

const PrivacyPolicy = ({ onHomeClick, onCategoriesClick, onHowItWorksClick }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('Usage Policies');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create navigation functions
  const handleHomeClick = () => navigate('/');
  const handleCategoriesClick = () => navigate('/#categories');
  const handleHowItWorksClick = () => navigate('/#how-it-works');
  const handleLoginClick = () => navigate('/login');
  const handleSignupClick = () => navigate('/signup');
  const handleProfileClick = () => navigate('/profile');
  const handleAccountSettingsClick = () => navigate('/settings');

  useEffect(() => {
    fetchPrivacyPolicy();
  }, []);

  const fetchPrivacyPolicy = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl('/settings/privacy_policy'));
      
      if (!response.ok) {
        throw new Error('Failed to fetch privacy policy');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setContent(data.data.content);
        setTitle(data.data.title);
      } else {
        throw new Error(data.message || 'Failed to load privacy policy');
      }
    } catch (err) {
      console.error('Error fetching privacy policy:', err);
      setError(err.message);
      // Fallback content
      setContent(`# Usage Policies

## 1. Introduction
Remote Birdy is committed to protecting your privacy. This Usage Policies document explains how we collect, use, disclose, and safeguard your information.

## 2. Information We Collect
We collect information you provide directly to us, such as name, email address, phone number, and profile information. We also automatically collect certain information when you use our platform.

## 3. How We Use Your Information
We use the information we collect to provide and maintain our services, process transactions, communicate with you, and improve our platform.

## 4. Information Sharing
We may share information with other users as part of our service, with service providers who assist us, and when required by law.

## 5. Data Security
We implement appropriate security measures to protect your information, including encryption and secure servers.

## 6. Your Rights
You can access and update your profile information, modify your privacy settings, and request account deletion.

## 7. Cookie Policy

### 7.1 What Are Cookies
Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better experience by remembering your preferences and understanding how you use our site.

### 7.2 Types of Cookies We Use
- **Essential Cookies**: These are necessary for the website to function properly. They enable core functionality such as security, network management, and accessibility.
- **Analytics Cookies**: These help us understand how visitors interact with our website by collecting and reporting information anonymously.
- **Functional Cookies**: These allow the website to remember choices you make (such as your username, language, or region) and provide enhanced, personalized features.
- **Marketing Cookies**: These are used to track visitors across websites to display relevant advertisements.

### 7.3 How We Use Cookies
We use cookies to:
- Remember your preferences and settings
- Analyze site traffic and usage patterns
- Improve website functionality and user experience
- Provide personalized content and advertisements
- Ensure website security

### 7.4 Managing Cookies
You can control and manage cookies in various ways. Most browsers allow you to refuse or accept cookies, and to delete cookies that have already been set. However, please note that disabling cookies may affect the functionality of our website.

### 7.5 Third-Party Cookies
Some cookies are placed by third-party services that appear on our pages. We do not control these cookies, and you should check the third-party websites for more information about their use of cookies.

## 8. Contact Us
For questions about these Usage Policies, please contact us at privacy@ravenhub.com

*Last updated: ${new Date().toLocaleDateString()}*`);
    } finally {
      setLoading(false);
    }
  };

  const renderMarkdown = (text) => {
    if (!text) return '';
    
    return text
      .split('\n')
      .map((line, index) => {
        // Handle headers
        if (line.startsWith('# ')) {
          return <h1 key={index} className="privacy-h1">{line.substring(2)}</h1>;
        }
        if (line.startsWith('## ')) {
          return <h2 key={index} className="privacy-h2">{line.substring(3)}</h2>;
        }
        if (line.startsWith('### ')) {
          return <h3 key={index} className="privacy-h3">{line.substring(4)}</h3>;
        }
        
        // Handle lists
        if (line.startsWith('- ')) {
          return <li key={index} className="privacy-li">{line.substring(2)}</li>;
        }
        
        // Handle italic text
        if (line.startsWith('*') && line.endsWith('*')) {
          return <p key={index} className="privacy-italic">{line.substring(1, line.length - 1)}</p>;
        }
        
        // Handle regular paragraphs
        if (line.trim() !== '') {
          return <p key={index} className="privacy-p">{line}</p>;
        }
        
        return <br key={index} />;
      });
  };

  if (loading) {
    return (
      <div className="privacy-policy-page">
        <Header 
          onLoginClick={handleLoginClick}
          onSignupClick={handleSignupClick}
          isLoggedIn={isAuthenticated}
          user={user}
          onHomeClick={handleHomeClick}
          onCategoriesClick={handleCategoriesClick}
          onHowItWorksClick={handleHowItWorksClick}
          onProfileClick={handleProfileClick}
          onAccountSettingsClick={handleAccountSettingsClick}
          currentPage="privacy-policy"
        />
        <div className="privacy-container">
          <div className="privacy-loading">
            <div className="spinner"></div>
            <p>Loading Usage Policies...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="privacy-policy-page">
      <Header 
        onLoginClick={handleLoginClick}
        onSignupClick={handleSignupClick}
        isLoggedIn={isAuthenticated}
        user={user}
        onHomeClick={handleHomeClick}
        onCategoriesClick={handleCategoriesClick}
        onHowItWorksClick={handleHowItWorksClick}
        onProfileClick={handleProfileClick}
        currentPage="privacy-policy"
      />
      
      <div className="privacy-container">
        <div className="privacy-header">
          <h1 className="privacy-title">{title}</h1>
          <div className="privacy-breadcrumb">
            <span onClick={handleHomeClick} className="breadcrumb-link">Home</span>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">Usage Policies</span>
          </div>
        </div>

        <div className="privacy-content">
          {error && (
            <div className="privacy-error">
              <p>⚠️ {error}</p>
              <p>Showing fallback content.</p>
            </div>
          )}
          
          <div className="privacy-text">
            {renderMarkdown(content)}
          </div>
        </div>

        <div className="privacy-footer">
          <div className="privacy-actions">
            <button 
              onClick={handleHomeClick}
              className="btn btn-primary"
            >
              Back to Home
            </button>
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="btn btn-secondary"
            >
              Back to Top
            </button>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
